/**
 * Pulse Voice — "in your words" evidence quote types + read-path parsing.
 *
 * Split out of `voice-catalog.ts` (which sits at its line cap) but conceptually
 * part of the same client-side mirror of `_shared/voice/{types,parse}.ts`. The
 * `set-voice-profile` edge function is the sole writer and sanitises on write, so
 * a stored profile is trustworthy — this is a boundary guard that coerces shape,
 * not a second sanitisation layer.
 */

/** A short verbatim quote from the writer's own sent mail ("in your words"). */
export interface VoiceEvidenceQuote {
  text: string;
  subject?: string;
  date?: string;
}

/** Persisted per-axis evidence quotes. Mirrors `_shared/voice/types.ts`. */
export interface VoiceEvidence {
  formality: VoiceEvidenceQuote[];
  warmth: VoiceEvidenceQuote[];
  length: VoiceEvidenceQuote[];
}

/** Mirror of the backend `MAX_QUOTES_PER_AXIS` (`_shared/voice/parse.ts`). */
const MAX_QUOTES_PER_AXIS = 3;

function readString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() !== '' ? value : undefined;
}

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

/** Coerce one stored evidence quote (the write path already sanitised it). */
function parseQuote(raw: unknown): VoiceEvidenceQuote | undefined {
  const r = asRecord(raw);
  const text = readString(r.text);
  if (!text) {
    return undefined;
  }
  const quote: VoiceEvidenceQuote = { text };
  const subject = readString(r.subject);
  if (subject) {
    quote.subject = subject;
  }
  const date = readString(r.date);
  if (date) {
    quote.date = date;
  }
  return quote;
}

/** Coerce one axis's stored quote list: drop empties, cap the count. */
function parseQuoteList(raw: unknown): VoiceEvidenceQuote[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .map(parseQuote)
    .filter((q): q is VoiceEvidenceQuote => q !== undefined)
    .slice(0, MAX_QUOTES_PER_AXIS);
}

/** Coerce the stored per-axis evidence block, or undefined when no axis has a quote. */
export function parseEvidenceValue(raw: unknown): VoiceEvidence | undefined {
  if (typeof raw !== 'object' || raw === null) {
    return undefined;
  }
  const r = raw as Record<string, unknown>;
  const evidence: VoiceEvidence = {
    formality: parseQuoteList(r.formality),
    warmth: parseQuoteList(r.warmth),
    length: parseQuoteList(r.length),
  };
  const hasAny =
    evidence.formality.length > 0 || evidence.warmth.length > 0 || evidence.length.length > 0;
  return hasAny ? evidence : undefined;
}
